<template>
  <div>
    <b-card>
      <b-row class="mb-2">
        <b-col
          class="d-flex"
          style="gap:10px"
        >
          <b-button
            variant="primary"
            @click="openNewModal"
          >
            + New Consultation
          </b-button>
          <b-button
            v-if="canManageVets"
            variant="outline-primary"
            @click="openAddVet"
          >
            + Add / Assign Vet
          </b-button>
        </b-col>
      </b-row>

      <b-tabs lazy>
        <b-tab
          title="Open"
          active
        >
          <div class="pt-2">
            <!-- Loading skeleton -->
            <div v-if="loading">
              <b-card class="mb-2">
                <b-skeleton
                  width="40%"
                  height="1.5rem"
                  class="mb-2"
                />
                <b-skeleton
                  width="70%"
                  class="mb-1"
                />
                <b-skeleton width="55%" />
              </b-card>
              <b-card class="mb-2">
                <b-skeleton
                  width="40%"
                  height="1.5rem"
                  class="mb-2"
                />
                <b-skeleton
                  width="70%"
                  class="mb-1"
                />
                <b-skeleton width="55%" />
              </b-card>
            </div>
            <div
              v-else-if="!openConsultations.length"
              class="text-center py-4 text-muted"
            >
              No open consultations.
            </div>
            <b-card
              v-for="c in openConsultations"
              :key="c.id"
              class="mb-2"
            >
              <b-row>
                <b-col md="8">
                  <div class="d-flex align-items-center mb-1">
                    <b-badge
                      variant="primary"
                      class="mr-2"
                    >
                      Open
                    </b-badge>
                    <strong>{{ c.chief_complaint }}</strong>
                  </div>
                  <p class="text-muted small mb-0">
                    Vet: {{ c.vet_name || (c.vet && c.vet.name) || '—' }} &bull; {{ formatDate(c.created_at) }}
                  </p>
                </b-col>
                <b-col
                  md="4"
                  class="d-flex align-items-center justify-content-end"
                >
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    :to="`/consultations/${c.id}`"
                  >
                    View
                  </b-button>
                </b-col>
              </b-row>
            </b-card>
          </div>
        </b-tab>

        <b-tab title="In Progress">
          <div class="pt-2">
            <!-- Loading skeleton -->
            <div v-if="loading">
              <b-card class="mb-2">
                <b-skeleton
                  width="40%"
                  height="1.5rem"
                  class="mb-2"
                />
                <b-skeleton
                  width="70%"
                  class="mb-1"
                />
                <b-skeleton width="55%" />
              </b-card>
            </div>
            <div
              v-else-if="!inProgressConsultations.length"
              class="text-center py-4 text-muted"
            >
              No in-progress consultations.
            </div>
            <b-card
              v-for="c in inProgressConsultations"
              :key="c.id"
              class="mb-2"
            >
              <b-row>
                <b-col md="8">
                  <div class="d-flex align-items-center mb-1">
                    <b-badge
                      variant="warning"
                      class="mr-2"
                    >
                      In Progress
                    </b-badge>
                    <strong>{{ c.chief_complaint }}</strong>
                  </div>
                  <p class="text-muted small mb-0">
                    Vet: {{ c.vet_name || (c.vet && c.vet.name) || '—' }} &bull; {{ formatDate(c.created_at) }}
                  </p>
                </b-col>
                <b-col
                  md="4"
                  class="d-flex align-items-center justify-content-end"
                >
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    :to="`/consultations/${c.id}`"
                  >
                    View
                  </b-button>
                </b-col>
              </b-row>
            </b-card>
          </div>
        </b-tab>

        <b-tab title="Closed">
          <div class="pt-2">
            <!-- Loading skeleton -->
            <div v-if="loading">
              <b-card class="mb-2">
                <b-skeleton
                  width="40%"
                  height="1.5rem"
                  class="mb-2"
                />
                <b-skeleton
                  width="70%"
                  class="mb-1"
                />
                <b-skeleton width="55%" />
              </b-card>
            </div>
            <div
              v-else-if="!closedConsultations.length"
              class="text-center py-4 text-muted"
            >
              No closed consultations.
            </div>
            <b-card
              v-for="c in closedConsultations"
              :key="c.id"
              class="mb-2"
            >
              <b-row>
                <b-col md="8">
                  <div class="d-flex align-items-center mb-1">
                    <b-badge
                      variant="secondary"
                      class="mr-2"
                    >
                      Closed
                    </b-badge>
                    <strong>{{ c.chief_complaint }}</strong>
                  </div>
                  <p class="text-muted small mb-0">
                    Vet: {{ c.vet_name || (c.vet && c.vet.name) || '—' }} &bull; {{ formatDate(c.created_at) }}
                  </p>
                </b-col>
                <b-col
                  md="4"
                  class="d-flex align-items-center justify-content-end"
                >
                  <b-button
                    variant="outline-secondary"
                    size="sm"
                    :to="`/consultations/${c.id}`"
                  >
                    View
                  </b-button>
                </b-col>
              </b-row>
            </b-card>
          </div>
        </b-tab>
      </b-tabs>

      <div
        v-if="totalRows > perPage"
        class="d-flex justify-content-end mt-2"
      >
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          @change="fetchData"
        />
      </div>
    </b-card>

    <!-- New Consultation Modal -->
    <b-modal
      v-model="showModal"
      title="New Consultation"
      ok-title="Submit"
      @ok="submitConsultation"
      @hidden="resetForm"
    >
      <b-form-group label="Select Vet">
        <b-form-select
          v-model="form.vet_id"
          :options="vetOptions"
          :state="errors.vet_id ? false : null"
        />
        <small
          v-if="errors.vet_id"
          class="text-danger d-block"
        >{{ errors.vet_id }}</small>
        <b-link
          v-if="canManageVets"
          class="small"
          @click="openAddVet"
        >
          + Add a new vet
        </b-link>
      </b-form-group>
      <b-form-group label="Flock (optional)">
        <b-form-input
          v-model="form.flock_id"
          placeholder="Flock ID (optional)"
        />
      </b-form-group>
      <b-form-group label="Chief Complaint">
        <b-form-textarea
          v-model="form.chief_complaint"
          rows="4"
          placeholder="Describe the issue (min 20 characters)..."
          :state="errors.chief_complaint ? false : null"
        />
        <small
          v-if="errors.chief_complaint"
          class="text-danger"
        >{{ errors.chief_complaint }}</small>
      </b-form-group>
    </b-modal>

    <!-- Add / Assign Vet Modal (phone-first, global dedup + approval) -->
    <b-modal
      id="add-vet-modal"
      v-model="showVetModal"
      title="Add or Assign a Vet"
      hide-footer
      @hidden="resetVet"
    >
      <p class="text-muted small mb-2">
        Vets are shared across companies. Enter the phone — if the vet already exists you can assign them instantly.
      </p>

      <!-- Step 1: phone + check -->
      <b-form-group label="Vet phone *">
        <div
          class="d-flex"
          style="gap:8px"
        >
          <b-form-input
            v-model="vet.phone"
            placeholder="Phone number"
            @input="lookup = null"
          />
          <b-button
            variant="primary"
            :disabled="checkingPhone || !vet.phone || !!lookup"
            @click="checkPhone"
          >
            <b-spinner
              v-if="checkingPhone"
              small
            /><span v-else>{{ lookup ? 'Checked' : 'Check' }}</span>
          </b-button>
        </div>
      </b-form-group>

      <!-- Step 2a: vet already exists -->
      <div
        v-if="lookup && lookup.exists"
        class="found-card mb-1"
      >
        <div class="font-weight-bold">
          {{ lookup.vet.name }}
        </div>
        <div class="text-muted small">
          {{ lookup.vet.specialization || '—' }}
        </div>
        <b-badge
          :variant="lookup.vet.approval === 'approved' ? 'light-success' : 'light-warning'"
          class="mt-50"
        >
          {{ lookup.vet.approval === 'approved' ? 'Approved' : 'Awaiting approval' }}
        </b-badge>
        <div
          v-if="lookup.already_assigned"
          class="text-success small mt-1"
        >
          ✓ Already in your vet list
        </div>
        <div
          v-else
          class="text-primary small mt-1"
        >
          {{ lookup.vet.approval === 'approved'
            ? 'Already in the system — assign to your company.'
            : 'In the system but awaiting admin approval. Assign now; consult once approved.' }}
        </div>
      </div>

      <!-- Step 2b: brand-new vet -->
      <div v-if="lookup && !lookup.exists">
        <b-alert
          variant="success"
          show
          class="p-1 small mb-1"
        >
          No vet with this number. Add them — an admin will approve the login.
        </b-alert>
        <b-form-group label="Name *">
          <b-form-input
            v-model="vet.name"
            placeholder="Dr. ..."
          />
        </b-form-group>
        <b-form-group label="Specialization">
          <b-form-input
            v-model="vet.specialization"
            placeholder="e.g. Poultry Health"
          />
        </b-form-group>
        <b-form-group label="Consultation Fee (₹)">
          <b-form-input
            v-model="vet.consultation_fee"
            type="number"
            placeholder="0"
          />
        </b-form-group>
      </div>

      <div
        class="d-flex justify-content-end mt-1"
        style="gap:10px"
      >
        <b-button
          variant="outline-secondary"
          @click="showVetModal = false"
        >
          Cancel
        </b-button>
        <b-button
          v-if="lookup && !(lookup.exists && lookup.already_assigned)"
          variant="primary"
          :disabled="savingVet"
          @click="submitVet"
        >
          <b-spinner
            v-if="savingVet"
            small
          />
          <span v-else>{{ lookup.exists ? 'Assign to my company' : 'Submit for approval' }}</span>
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from '@/libs/axios'
import { getUserData } from '@/auth/utils'
import moment from 'moment'
import {
	BCard, BRow, BCol, BButton, BBadge, BSpinner, BModal, BLink, BAlert,
	BTabs, BTab, BFormGroup, BFormInput, BFormSelect, BFormTextarea, BPagination,
} from 'bootstrap-vue'

export default {
	name: 'ConsultationList',
	components: {
		BCard,
		BRow,
		BCol,
		BButton,
		BBadge,
		BSpinner,
		BModal,
		BLink,
		BAlert,
		BTabs,
		BTab,
		BFormGroup,
		BFormInput,
		BFormSelect,
		BFormTextarea,
		BPagination,
	},
	data() {
		return {
			loading: false,
			consultations: [],
			vets: [],
			showModal: false,
			errors: {},
			form: { vet_id: '', flock_id: '', chief_complaint: '' },
			currentPage: 1,
			totalRows: 0,
			perPage: 25,
			// Add / assign vet
			showVetModal: false,
			checkingPhone: false,
			savingVet: false,
			lookup: null,
			vet: {
				name: '', phone: '', specialization: '', consultation_fee: '',
			},
		}
	},
	computed: {
		canManageVets() {
			const role = getUserData()?.role
			return role === 'owner' || role === 'admin'
		},
		openConsultations() {
			return this.consultations.filter(c => c.status === 'open')
		},
		inProgressConsultations() {
			return this.consultations.filter(c => c.status === 'in_progress')
		},
		closedConsultations() {
			return this.consultations.filter(c => c.status === 'closed')
		},
		vetOptions() {
			const opts = [{ value: '', text: '-- Select Vet --' }]
			return opts.concat(this.vets.map(v => ({ value: v.id, text: v.name })))
		},
	},
	created() {
		this.fetchData()
		this.fetchVets()
	},
	methods: {
		formatDate(value) {
			return value ? moment(value).format('MMMM Do YYYY, h:mm A') : '—'
		},
		async fetchData(page = 1) {
			this.loading = true
			try {
				const { data } = await axios.get('web/vet-consultations', { params: { page } })
				// Handle both paginated ({data: [...], total, per_page}) and plain array responses
				if (data && data.data && Array.isArray(data.data)) {
					this.consultations = data.data
					this.totalRows = (data.meta && data.meta.total) || data.total || data.data.length
					this.perPage = (data.meta && data.meta.per_page) || data.per_page || 25
					this.currentPage = (data.meta && data.meta.current_page) || data.current_page || page
				} else {
					this.consultations = data.items || data || []
					this.totalRows = this.consultations.length
				}
			} catch (e) {
				console.error(e)
				this.$bvToast.toast('Failed to load consultations', { variant: 'danger', title: 'Error' })
			} finally {
				this.loading = false
			}
		},
		async fetchVets() {
			try {
				const { data } = await axios.get('web/vets')
				this.vets = data.data || data.items || data || []
			} catch (e) {
				console.error(e)
			}
		},
		openNewModal() {
			this.resetForm()
			this.showModal = true
		},
		resetForm() {
			this.form = { vet_id: '', flock_id: '', chief_complaint: '' }
			this.errors = {}
		},
		validateForm() {
			this.errors = {}
			if (!this.form.chief_complaint || this.form.chief_complaint.trim().length < 20) {
				this.errors.chief_complaint = 'Chief complaint must be at least 20 characters'
			}
			if (!this.form.vet_id) {
				this.errors.vet_id = 'Please select a vet'
			}
			return Object.keys(this.errors).length === 0
		},
		async submitConsultation(bvModalEvt) {
			bvModalEvt.preventDefault()
			if (!this.validateForm()) return
			try {
				await axios.post('web/vet-consultations', this.form)
				this.$bvToast.toast('Consultation submitted', { variant: 'success', title: 'Success' })
				this.showModal = false
				this.fetchData()
			} catch (e) {
				console.error(e)
				this.$bvToast.toast('Failed to submit consultation', { variant: 'danger', title: 'Error' })
			}
		},

		// ── Add / assign vet (global, phone-deduped, approval-gated) ──────────
		openAddVet() {
			this.resetVet()
			this.showVetModal = true
		},
		resetVet() {
			this.lookup = null
			this.vet = {
				name: '', phone: '', specialization: '', consultation_fee: '',
			}
		},
		async checkPhone() {
			const phone = (this.vet.phone || '').trim()
			if (!phone) return
			this.checkingPhone = true
			try {
				const { data } = await axios.post('web/vets/lookup', { phone })
				this.lookup = {
					exists: !!data.exists,
					already_assigned: data.already_assigned,
					vet: data.vet || {},
				}
			} catch (e) {
				this.$bvToast.toast(e.response?.data?.message || 'Could not check the number', { variant: 'danger', title: 'Error' })
			} finally {
				this.checkingPhone = false
			}
		},
		async submitVet() {
			const phone = (this.vet.phone || '').trim()
			if (!phone) return
			if (!this.lookup?.exists && !this.vet.name.trim()) {
				this.$bvToast.toast('Vet name is required for a new vet', { variant: 'warning', title: 'Missing name' })
				return
			}
			this.savingVet = true
			try {
				const { data } = await axios.post('web/vets', {
					name: this.vet.name.trim() || this.lookup?.vet?.name || 'Vet',
					phone,
					specialization: this.vet.specialization.trim() || undefined,
					consultation_fee: this.vet.consultation_fee ? Number(this.vet.consultation_fee) : undefined,
				})
				await this.fetchVets()
				if (data.assigned && data.approval === 'approved' && data.data?.id) {
					this.form.vet_id = data.data.id
				}
				this.showVetModal = false
				this.$bvToast.toast(data.message || 'Done', {
					variant: data.assigned ? 'success' : 'info',
					title: data.assigned ? 'Vet assigned' : 'Sent for approval',
				})
			} catch (e) {
				this.$bvToast.toast(e.response?.data?.message || 'Failed to add vet', { variant: 'danger', title: 'Error' })
			} finally {
				this.savingVet = false
			}
		},
	},
}
</script>

<style scoped>
.found-card {
  border: 1px solid #296DB4;
  background: rgba(41, 109, 180, 0.06);
  border-radius: 6px;
  padding: 12px 14px;
}
</style>
